import { useMemo } from 'react'
import { create } from 'zustand'

import { useShareholders } from '@/features/portfolio/api/queries'
import type { ShareholderProfile } from '@/schemas/portfolio'

/**
 * Which book the portfolio screens are currently scoped to.
 *
 * ── ACTIVE is not PRIMARY ────────────────────────────────────────────────────
 *
 * These are two different things and this file is where the difference is kept
 * honest:
 *
 *   PRIMARY  a server flag (`ShareholderProfile.isPrimary`). Exactly one
 *            profile carries it, it is what `Home` reads, and it is what every
 *            unscoped portfolio request resolves to (`primary_or_first`).
 *            Changing it is a WRITE — `POST /shareholders/{id}/primary/`.
 *   ACTIVE   a client-side view choice: which book the member is looking at
 *            right now. Changing it writes nothing and moves no flag.
 *
 * The Figma switcher (`3448:34489`) conflates them — it draws one checkmark and
 * nothing else — so the two would silently merge if this store did not keep
 * them apart. `PortfolioSwitcherMenu` renders the checkmark for ACTIVE and a
 * separate "· Primary" suffix for the flag, so a member viewing "Family book"
 * while "My Portfolio" is still primary can see both facts at once.
 *
 * ── Why `chosenId` is nullable, and why `scopeId` is not just the id ─────────
 *
 * `null` means "the member has not picked one; follow the server's primary".
 * That is the first-run state and the state after a delete, and it is NOT the
 * same as "the primary profile's id" — see `scopeId` below.
 *
 * Deliberately NOT persisted. A remembered id survives the profile being
 * deleted on another device, and the resolver below would then silently fall
 * back to the primary book while the member believes they are still in the one
 * they picked. A session-scoped choice cannot go stale that way.
 */
interface ActiveProfileState {
  /** The profile the member explicitly switched to. `null` = follow primary. */
  chosenId: number | null
  choose: (id: number | null) => void
}

export const useActiveProfileStore = create<ActiveProfileState>()((set) => ({
  chosenId: null,
  choose: (chosenId) => {
    set({ chosenId })
  },
}))

export interface ActiveProfile {
  /** The resolved book, or `undefined` when the member has no profile at all. */
  profile: ShareholderProfile | undefined
  /**
   * What to pass to the scoped portfolio queries — and `undefined` for the
   * primary book, deliberately.
   *
   * `portfolioKeys.book(undefined)` keys as `'primary'`, and every portfolio
   * screen reads the unscoped queries today. Passing the primary profile's own
   * id would cache the identical rows a second time under `book(4)` while the
   * screens sit on `book('primary')` — two live copies of one book, and a write
   * that invalidated one would leave the other stale on screen. So the primary
   * book is always addressed the way the screens already address it.
   *
   * This is the same rule `AddTransactionModal` applies to its own writes; it
   * is restated rather than imported because that modal keeps its scope in
   * local state and this one is app-wide.
   */
  scopeId: number | undefined
}

/**
 * Pure resolution, exported for its own test and for callers that already hold
 * the list.
 *
 * The fallback chain is `chosen → primary → first`, matching the order the API
 * itself resolves an unscoped request in (`primary_or_first`). A `chosenId`
 * that matches nothing — the profile was deleted, or renamed away on another
 * device — falls through rather than erroring, so a stale choice degrades to
 * the primary book instead of an empty screen.
 */
export const resolveActiveProfile = (
  profiles: readonly ShareholderProfile[],
  chosenId: number | null,
): ActiveProfile => {
  const profile =
    profiles.find((candidate) => candidate.id === chosenId) ??
    profiles.find((candidate) => candidate.isPrimary) ??
    profiles[0]

  return {
    profile,
    scopeId: profile === undefined || profile.isPrimary ? undefined : profile.id,
  }
}

/** The resolved active book, given the list the caller has already loaded. */
export const useActiveProfile = (profiles: readonly ShareholderProfile[]): ActiveProfile => {
  const chosenId = useActiveProfileStore((state) => state.chosenId)
  return useMemo(() => resolveActiveProfile(profiles, chosenId), [profiles, chosenId])
}

/**
 * The scope id for the per-profile portfolio reads, for callers that do not
 * already hold the profile list.
 *
 * This is the one line each portfolio screen needs to become switchable:
 * `usePortfolioOverview(useActiveShareholderId())` instead of
 * `usePortfolioOverview()`. It reads the same cached `useShareholders` query
 * the header does, so adopting it costs no extra request.
 */
export const useActiveShareholderId = (): number | undefined => {
  const { data } = useShareholders()
  const profiles = useMemo(() => data ?? [], [data])
  return useActiveProfile(profiles).scopeId
}
